import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthLayout from "../components/AuthLayout";
import { Button } from "../components/ui/Button";
import { Input, Label } from "../components/ui/Input";
import { Lock, CheckCircle, Warning } from "@phosphor-icons/react";
import { api, extractError } from "../lib/api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const tooShort = pw1.length > 0 && pw1.length < 8;
  const mismatch = pw2.length > 0 && pw1 !== pw2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Der Reset-Link enthält keinen gültigen Token.");
      return;
    }
    if (pw1.length < 8) {
      setError("Das neue Passwort muss mindestens 8 Zeichen haben.");
      return;
    }
    if (pw1 !== pw2) {
      setError("Die beiden Passwörter stimmen nicht überein.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (err) {
      setError(extractError(err, "Passwort konnte nicht zurückgesetzt werden."));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthLayout
        title="Passwort gesetzt"
        subtitle="Sie können sich jetzt mit dem neuen Passwort anmelden."
      >
        <div
          data-testid="reset-success"
          className="flex items-start gap-3 border border-status-aktiv-border bg-status-aktiv-bg p-4 rounded-sm"
        >
          <CheckCircle size={18} weight="fill" className="text-status-aktiv-text mt-0.5 shrink-0" />
          <div className="text-sm text-ink-900">
            Ihr Passwort wurde erfolgreich aktualisiert. Sie werden zur
            Anmeldung weitergeleitet …
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Neues Passwort setzen"
      subtitle="Bitte vergeben Sie ein neues Passwort für Ihr ET-Konto."
      footer={
        <p className="text-xs text-warm-600 text-center">
          <Link
            to="/login"
            data-testid="reset-back-login"
            className="font-semibold text-ink-900 hover:text-brand-500 transition-crisp"
          >
            Zurück zur Anmeldung
          </Link>
        </p>
      }
    >
      {!token && (
        <div
          data-testid="reset-no-token"
          role="alert"
          className="mb-4 flex items-start gap-2 border border-status-abgelehnt-border bg-status-abgelehnt-bg px-3 py-2 rounded-sm text-sm text-status-abgelehnt-text"
        >
          <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
          <span>Der Reset-Link ist unvollständig. Bitte fordern Sie einen neuen an.</span>
        </div>
      )}

      <form
        className="space-y-5"
        onSubmit={handleSubmit}
        noValidate
        data-testid="reset-form"
      >
        <div>
          <Label htmlFor="pw1">Neues Passwort</Label>
          <div className="relative">
            <Lock
              size={16}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-400 pointer-events-none"
            />
            <Input
              id="pw1"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              maxLength={128}
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              placeholder="mindestens 8 Zeichen"
              className="pl-9"
              data-testid="reset-pw1-input"
            />
          </div>
          {tooShort && (
            <p className="mt-1 text-[11px] text-status-abgelehnt-text">
              Mindestens 8 Zeichen erforderlich.
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="pw2">Passwort bestätigen</Label>
          <div className="relative">
            <Lock
              size={16}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-400 pointer-events-none"
            />
            <Input
              id="pw2"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              maxLength={128}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="zur Bestätigung"
              className="pl-9"
              data-testid="reset-pw2-input"
            />
          </div>
          {mismatch && (
            <p className="mt-1 text-[11px] text-status-abgelehnt-text">
              Die Passwörter stimmen nicht überein.
            </p>
          )}
        </div>

        {error && (
          <div
            data-testid="reset-error"
            role="alert"
            className="flex items-start gap-2 border border-status-abgelehnt-border bg-status-abgelehnt-bg px-3 py-2 rounded-sm text-sm text-status-abgelehnt-text"
          >
            <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button
          type="submit"
          variant="brand"
          size="lg"
          className="w-full"
          disabled={submitting || !pw1 || !pw2 || !token}
          data-testid="reset-submit-btn"
        >
          {submitting ? "Wird gesetzt…" : "Passwort jetzt setzen"}
        </Button>
      </form>
    </AuthLayout>
  );
}
